/* ============================================================================
   BrainHero — a real-time WebGL particle brain for OptiMind.
   A glowing synaptic point-cloud that rotates, twinkles, fires synapses,
   ignites under the cursor, bursts on click, and emits energy embers that
   drift toward the product. Depends on global THREE (r0.150 UMD).

   Usage:
     const b = new BrainHero({ canvas, warm:'#ff7a2f', cool:'#37b6ff',
        density:1, motion:1, glow:1, offset:[x,y], scale:1,
        emitDir:[x,y], interactive:true });
     b.start();  b.pause();  b.dispose();
     b.setColors(warm, cool);  b.setMotion(v);  b.setGlow(v);  b.setDensity(v);
   ========================================================================== */
(function (global) {
  'use strict';
  const T = global.THREE;

  /* ---- cheap CPU value-noise for fold displacement ---- */
  const fract = (x) => x - Math.floor(x);
  function hash3(x, y, z) { return fract(Math.sin(x * 127.1 + y * 311.7 + z * 74.7) * 43758.5453); }
  function vnoise(x, y, z) {
    const xi = Math.floor(x), yi = Math.floor(y), zi = Math.floor(z);
    const xf = x - xi, yf = y - yi, zf = z - zi;
    const u = xf * xf * (3 - 2 * xf), v = yf * yf * (3 - 2 * yf), w = zf * zf * (3 - 2 * zf);
    const L = (a, b, t) => a + (b - a) * t;
    const c000 = hash3(xi, yi, zi), c100 = hash3(xi + 1, yi, zi), c010 = hash3(xi, yi + 1, zi), c110 = hash3(xi + 1, yi + 1, zi);
    const c001 = hash3(xi, yi, zi + 1), c101 = hash3(xi + 1, yi, zi + 1), c011 = hash3(xi, yi + 1, zi + 1), c111 = hash3(xi + 1, yi + 1, zi + 1);
    return L(L(L(c000, c100, u), L(c010, c110, u), v), L(L(c001, c101, u), L(c011, c111, u), v), w);
  }
  function fbm(x, y, z) { let f = 0, a = 0.5; for (let i = 0; i < 4; i++) { f += a * vnoise(x, y, z); x *= 2; y *= 2; z *= 2; a *= 0.5; } return f; }
  const smoothstep = (e0, e1, x) => { const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0))); return t * t * (3 - 2 * t); };

  /* ---- soft round sprite for signals / embers ---- */
  function softTexture() {
    const c = document.createElement('canvas'); c.width = c.height = 64;
    const g = c.getContext('2d');
    const grd = g.createRadialGradient(32, 32, 0, 32, 32, 32);
    grd.addColorStop(0, 'rgba(255,255,255,1)');
    grd.addColorStop(0.25, 'rgba(255,255,255,0.85)');
    grd.addColorStop(0.55, 'rgba(255,255,255,0.25)');
    grd.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grd; g.fillRect(0, 0, 64, 64);
    const tex = new T.Texture(c); tex.needsUpdate = true; return tex;
  }

  const VERT = `
    attribute float aSize; attribute float aPhase; attribute float aFlare; attribute float aRad;
    attribute vec3 aColor;
    uniform float uTime, uPixelRatio, uMouseOn, uBurst, uMotion, uGlow;
    uniform vec2 uMouse;
    varying vec3 vColor; varying float vBright;
    void main(){
      vColor = aColor;
      vec4 mv = modelViewMatrix * vec4(position,1.0);
      vec4 proj = projectionMatrix * mv;
      float tw = 0.6 + 0.4*sin(uTime*2.0*uMotion + aPhase*6.2831);
      float fire = aFlare * (0.45 + 0.55*sin(uTime*3.0*uMotion + aPhase*10.0));
      float bright = 0.24 + 0.24*tw + fire*0.7;
      vec2 sp = proj.xy/proj.w;
      float md = distance(sp, uMouse);
      float ignite = uMouseOn * smoothstep(0.42,0.0,md);
      bright += ignite*1.1;
      float ringR = uBurst*1.9;
      float ring = exp(-pow((aRad-ringR)*3.2,2.0)) * exp(-uBurst*1.1);
      bright += ring*1.6;
      vBright = bright * (0.6 + 0.4*uGlow);
      float size = aSize*(1.0 + fire*0.85 + ignite*1.6 + ring*1.7) * (0.85+0.3*uGlow);
      gl_PointSize = size * uPixelRatio * (13.0 / -mv.z);
      gl_Position = proj;
    }`;
  const FRAG = `
    precision mediump float;
    varying vec3 vColor; varying float vBright;
    void main(){
      vec2 uv = gl_PointCoord - 0.5;
      float d = length(uv);
      float alpha = pow(smoothstep(0.5,0.0,d), 2.0);
      vec3 col = vColor * vBright;
      col += vec3(1.0) * smoothstep(0.10,0.0,d) * vBright * 0.18;
      gl_FragColor = vec4(col, alpha);
      if(alpha < 0.01) discard;
    }`;

  function BrainHero(opts) {
    this.o = Object.assign({
      warm: '#ff7a2f', cool: '#37b6ff', density: 1, motion: 1, glow: 1,
      offset: [0, 0], scale: 1, emitDir: [-0.7, -0.9], interactive: true, dpr: 1.4
    }, opts);
    this.canvas = opts.canvas;
    this.running = false; this.time = 0; this.last = 0;
    this.mouse = new T.Vector2(0, 0); this.mouseTarget = new T.Vector2(0, 0); this.mouseOn = 0;
    this.tilt = new T.Vector2(0, 0); this.tiltT = new T.Vector2(0, 0);
    this.burst = 99; this.bursting = false;
    this._init();
  }

  BrainHero.prototype._init = function () {
    const o = this.o, cv = this.canvas;
    const w = cv.clientWidth || 800, h = cv.clientHeight || 600;
    this.renderer = new T.WebGLRenderer({ canvas: cv, antialias: true, alpha: true, powerPreference: 'high-performance', preserveDrawingBuffer: true });
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.setPixelRatio(Math.min(global.devicePixelRatio || 1, o.dpr));
    this.renderer.setSize(w, h, false);
    this.scene = new T.Scene();
    this.camera = new T.PerspectiveCamera(38, w / h, 0.1, 100);
    this.camera.position.set(0, 0, 4.6);
    this.group = new T.Group();
    this.group.position.set(o.offset[0], o.offset[1], 0);
    this.group.scale.setScalar(o.scale);
    this.scene.add(this.group);

    this.warm = new T.Color(o.warm);
    this.cool = new T.Color(o.cool);
    this.hot = new T.Color('#ffe7c2');

    this._buildBrain();
    this._buildLines();
    this._buildSignals();
    if (this.o.embers !== false) this._buildEmbers();
    if (this.o.fuel) this._buildFuel();

    this._onResize = this._resize.bind(this);
    if (global.ResizeObserver) { this.ro = new global.ResizeObserver(this._onResize); this.ro.observe(cv); }
    global.addEventListener('resize', this._onResize);

    if (o.interactive) {
      this._onMove = (e) => {
        const r = cv.getBoundingClientRect();
        const x = ((e.clientX - r.left) / r.width) * 2 - 1;
        const y = -(((e.clientY - r.top) / r.height) * 2 - 1);
        this.mouseTarget.set(x, y); this.mouseOn = 1;
        this.tiltT.set(x * 0.28, y * 0.2);
      };
      this._onLeave = () => { this.mouseOn = 0; this.tiltT.set(0, 0); };
      this._onDown = () => { this.burst = 0; this.bursting = true; };
      cv.addEventListener('pointermove', this._onMove);
      cv.addEventListener('pointerleave', this._onLeave);
      cv.addEventListener('pointerdown', this._onDown);
    }
  };

  BrainHero.prototype._brainPoint = function () {
    const u = Math.random(), v = Math.random();
    const theta = Math.acos(2 * u - 1), phi = 2 * Math.PI * v;
    let dx = Math.sin(theta) * Math.cos(phi), dy = Math.cos(theta), dz = Math.sin(theta) * Math.sin(phi);
    const ax = 1.14, ay = 0.84, az = 1.24;
    let x = dx * ax, y = dy * ay, z = dz * az;
    const n = fbm(x * 2.5 + 11, y * 2.5 + 3, z * 2.5 + 7);
    const fold = 0.82 + 0.24 * n;
    x *= fold; y *= fold; z *= fold;
    // longitudinal fissure: carve a groove down the top midline
    const groove = smoothstep(0.0, 0.16, Math.abs(x));
    if (y > 0) y -= (1 - groove) * 0.22 * smoothstep(0.0, 1.0, y);
    // frontal lobe lift / temporal flatten
    y *= 0.90 + 0.12 * smoothstep(-0.5, 0.7, z);
    // a little volume scatter so it isn't a hollow shell
    const rr = 0.9 + 0.1 * Math.random();
    x *= rr; y *= rr; z *= rr;
    return [x, y, z, n];
  };

  BrainHero.prototype._buildBrain = function () {
    const N = Math.round(6800 * this.o.density);
    this.N = N;
    const pos = new Float32Array(N * 3), col = new Float32Array(N * 3);
    const size = new Float32Array(N), phase = new Float32Array(N), flare = new Float32Array(N), rad = new Float32Array(N);
    const c = new T.Color();
    this.nodes = [];
    for (let i = 0; i < N; i++) {
      let p;
      if (i > N * 0.955) {
        // brain stem / cerebellum tail tapering down-back
        const f = Math.random();
        const sa = Math.random() * Math.PI * 2;
        const sr = 0.20 * (1 - f) + 0.03;
        p = [Math.cos(sa) * sr, -0.62 - f * 0.72, Math.sin(sa) * sr - 0.12, 0.6];
      } else {
        p = this._brainPoint();
      }
      pos[i * 3] = p[0]; pos[i * 3 + 1] = p[1]; pos[i * 3 + 2] = p[2];
      const r = Math.sqrt(p[0] * p[0] + p[1] * p[1] + p[2] * p[2]);
      rad[i] = Math.min(1, r / 1.5);
      // colour: amber-dominant with cyan threading the core/front + sparks
      const tcool = smoothstep(0.1, 0.92, 0.5 + 0.25 * p[2] + 0.7 * (p[3] - 0.5));
      c.copy(this.warm).lerp(this.cool, Math.pow(tcool, 1.4) * 0.7);
      const spark = Math.random();
      if (spark > 0.986) c.copy(this.hot);
      else if (spark > 0.93) c.copy(this.cool);
      col[i * 3] = c.r; col[i * 3 + 1] = c.g; col[i * 3 + 2] = c.b;
      size[i] = 1.3 + Math.random() * 2.0;
      phase[i] = Math.random();
      // ~9% of points are firing synapse nodes (bigger, flare)
      const isNode = Math.random() > 0.93;
      flare[i] = isNode ? 0.6 + Math.random() * 0.5 : 0.0;
      if (isNode) { size[i] += 1.8; this.nodes.push([p[0], p[1], p[2]]); }
    }
    const g = new T.BufferGeometry();
    g.setAttribute('position', new T.BufferAttribute(pos, 3));
    g.setAttribute('aColor', new T.BufferAttribute(col, 3));
    g.setAttribute('aSize', new T.BufferAttribute(size, 1));
    g.setAttribute('aPhase', new T.BufferAttribute(phase, 1));
    g.setAttribute('aFlare', new T.BufferAttribute(flare, 1));
    g.setAttribute('aRad', new T.BufferAttribute(rad, 1));
    this.uniforms = {
      uTime: { value: 0 }, uPixelRatio: { value: this.renderer.getPixelRatio() },
      uMouse: { value: this.mouse }, uMouseOn: { value: 0 }, uBurst: { value: 99 },
      uMotion: { value: this.o.motion }, uGlow: { value: this.o.glow }
    };
    const m = new T.ShaderMaterial({
      uniforms: this.uniforms, vertexShader: VERT, fragmentShader: FRAG,
      transparent: true, blending: T.AdditiveBlending, depthTest: false, depthWrite: false
    });
    this.points = new T.Points(g, m);
    this.brainGeo = g;
    this.group.add(this.points);
  };

  BrainHero.prototype._buildLines = function () {
    // connect near node pairs with faint additive segments
    const nodes = this.nodes; const segs = [];
    const maxLines = 240; let count = 0;
    for (let i = 0; i < nodes.length && count < maxLines; i++) {
      for (let j = i + 1; j < nodes.length && count < maxLines; j++) {
        const a = nodes[i], b = nodes[j];
        const dx = a[0] - b[0], dy = a[1] - b[1], dz = a[2] - b[2];
        const d2 = dx * dx + dy * dy + dz * dz;
        if (d2 < 0.16 && Math.random() > 0.55) { segs.push(a, b); count++; }
      }
    }
    this.segPairs = segs;
    const pos = new Float32Array(segs.length * 3), col = new Float32Array(segs.length * 3);
    const c = new T.Color();
    for (let i = 0; i < segs.length; i++) {
      pos[i * 3] = segs[i][0]; pos[i * 3 + 1] = segs[i][1]; pos[i * 3 + 2] = segs[i][2];
      c.copy(this.cool).lerp(this.warm, Math.random() * 0.5);
      col[i * 3] = c.r; col[i * 3 + 1] = c.g; col[i * 3 + 2] = c.b;
    }
    const g = new T.BufferGeometry();
    g.setAttribute('position', new T.BufferAttribute(pos, 3));
    g.setAttribute('color', new T.BufferAttribute(col, 3));
    const m = new T.LineBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.20, blending: T.AdditiveBlending, depthTest: false, depthWrite: false });
    this.lines = new T.LineSegments(g, m); this.lineMat = m;
    this.group.add(this.lines);
  };

  BrainHero.prototype._buildSignals = function () {
    // bright sprites travelling along the segments
    const pairs = this.segPairs; const M = Math.min(46, Math.floor(pairs.length / 2));
    this.signals = [];
    const pos = new Float32Array(M * 3);
    for (let i = 0; i < M; i++) {
      const s = (Math.floor(Math.random() * (pairs.length / 2))) * 2;
      this.signals.push({ s, t: Math.random(), sp: 0.5 + Math.random() * 1.2 });
      pos[i * 3] = 0; pos[i * 3 + 1] = 0; pos[i * 3 + 2] = 0;
    }
    const g = new T.BufferGeometry();
    g.setAttribute('position', new T.BufferAttribute(pos, 3));
    this.tex = softTexture();
    const m = new T.PointsMaterial({ size: 0.085, map: this.tex, color: new T.Color(this.o.warm).lerp(this.hot, 0.5), transparent: true, blending: T.AdditiveBlending, depthTest: false, depthWrite: false, sizeAttenuation: true });
    this.sigMat = m;
    this.sigPoints = new T.Points(g, m);
    this.group.add(this.sigPoints);
  };

  BrainHero.prototype._buildEmbers = function () {
    const E = 110; this.E = E;
    this.embers = [];
    const dir = this.o.emitDir;
    for (let i = 0; i < E; i++) this.embers.push(this._spawnEmber(dir, true));
    const pos = new Float32Array(E * 3);
    const g = new T.BufferGeometry();
    g.setAttribute('position', new T.BufferAttribute(pos, 3));
    const m = new T.PointsMaterial({ size: 0.07, map: this.tex || softTexture(), color: new T.Color(this.o.warm), transparent: true, opacity: 0.9, blending: T.AdditiveBlending, depthTest: false, depthWrite: false, sizeAttenuation: true });
    this.emberMat = m;
    this.emberPoints = new T.Points(g, m);
    this.group.add(this.emberPoints);
  };

  BrainHero.prototype._spawnEmber = function (dir) {
    // start near the lower portion of the brain, drift toward emit direction
    const a = Math.random() * Math.PI * 2;
    return {
      x: Math.cos(a) * 0.5, y: -0.3 - Math.random() * 0.5, z: Math.sin(a) * 0.4 + 0.2,
      vx: dir[0] * (0.12 + Math.random() * 0.1) + (Math.random() - 0.5) * 0.04,
      vy: dir[1] * (0.10 + Math.random() * 0.08) + (Math.random() - 0.5) * 0.03,
      vz: (Math.random() - 0.5) * 0.05,
      life: 0, max: 2.4 + Math.random() * 2.4
    };
  };

  BrainHero.prototype._buildFuel = function () {
    // energy streams that flow FROM the spilled tablets INTO the brain
    const K = 160; this.K = K;
    this.fuelOrigin = new T.Vector3();
    const o = this.o.fuelOrigin || [1.6, -1.95, 0.5];
    this.fuelOrigin.set(o[0], o[1], o[2]);
    this.fuelTarget = this.group.position.clone();
    this.fuelParts = [];
    for (let i = 0; i < K; i++) this.fuelParts.push(this._spawnFuel());
    const pos = new Float32Array(K * 3), col = new Float32Array(K * 3);
    const g = new T.BufferGeometry();
    g.setAttribute('position', new T.BufferAttribute(pos, 3));
    g.setAttribute('color', new T.BufferAttribute(col, 3));
    const m = new T.PointsMaterial({ size: 0.13, map: this.tex || softTexture(), vertexColors: true, transparent: true, opacity: 1, blending: T.AdditiveBlending, depthTest: false, depthWrite: false, sizeAttenuation: true });
    this.fuelMat = m;
    this.fuelGroup = new T.Group();
    this.fuelPoints = new T.Points(g, m);
    this.fuelGroup.add(this.fuelPoints);
    this.scene.add(this.fuelGroup);
  };

  BrainHero.prototype._spawnFuel = function () {
    return {
      t: Math.random(), sp: 0.32 + Math.random() * 0.42,
      ax: (Math.random() - 0.5) * 2, ay: (Math.random() - 0.5) * 2,
      seed: Math.random() * 6.283, sz: 0.5 + Math.random() * 0.9
    };
  };

  BrainHero.prototype._resize = function () {
    const cv = this.canvas; const w = cv.clientWidth, h = cv.clientHeight;
    if (!w || !h) return;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h; this.camera.updateProjectionMatrix();
    if (this.uniforms) this.uniforms.uPixelRatio.value = this.renderer.getPixelRatio();
  };

  BrainHero.prototype._frame = function (now) {
    if (!this.running) return;
    const dt = Math.min(0.05, (now - this.last) / 1000 || 0.016); this.last = now;
    this.time += dt * this.o.motion;
    const u = this.uniforms;
    u.uTime.value = this.time;
    u.uMotion.value = this.o.motion; u.uGlow.value = this.o.glow;

    // smooth mouse + tilt
    this.mouse.lerp(this.mouseTarget, 0.12);
    this.mouseOnS = (this.mouseOnS || 0) + (this.mouseOn - (this.mouseOnS || 0)) * 0.1;
    u.uMouse.value.copy(this.mouse); u.uMouseOn.value = this.mouseOnS;
    this.tilt.lerp(this.tiltT, 0.06);
    this.group.rotation.y += dt * 0.12 * this.o.motion;
    this.group.rotation.x = this.tilt.y * 0.6;
    this.group.rotation.z = this.tilt.x * 0.12;

    // burst
    if (this.bursting) { this.burst += dt * 1.4; if (this.burst > 1.7) { this.bursting = false; this.burst = 99; } }
    u.uBurst.value = this.burst;

    // line shimmer
    if (this.lineMat) this.lineMat.opacity = 0.14 + 0.08 * (0.5 + 0.5 * Math.sin(this.time * 1.5));

    // signals along segments
    const pairs = this.segPairs, sg = this.sigPoints.geometry.attributes.position;
    if (pairs.length > 1) {
      for (let i = 0; i < this.signals.length; i++) {
        const s = this.signals[i]; s.t += dt * s.sp;
        if (s.t > 1) { s.t = 0; s.s = (Math.floor(Math.random() * (pairs.length / 2))) * 2; }
        const a = pairs[s.s], b = pairs[s.s + 1];
        if (a && b) {
          sg.array[i * 3] = a[0] + (b[0] - a[0]) * s.t;
          sg.array[i * 3 + 1] = a[1] + (b[1] - a[1]) * s.t;
          sg.array[i * 3 + 2] = a[2] + (b[2] - a[2]) * s.t;
        }
      }
      sg.needsUpdate = true;
    }

    // embers
    if (this.emberPoints) {
      const ep = this.emberPoints.geometry.attributes.position;
      for (let i = 0; i < this.E; i++) {
        const e = this.embers[i]; e.life += dt;
        e.x += e.vx * dt; e.y += e.vy * dt; e.z += e.vz * dt;
        e.vy -= 0.02 * dt;
        if (e.life > e.max) this.embers[i] = this._spawnEmber(this.o.emitDir);
        ep.array[i * 3] = e.x; ep.array[i * 3 + 1] = e.y; ep.array[i * 3 + 2] = e.z;
      }
      ep.needsUpdate = true;
    }

    // fuel: tablets -> brain inflow streams
    if (this.fuelPoints) {
      const fp = this.fuelPoints.geometry.attributes.position;
      const fc = this.fuelPoints.geometry.attributes.color;
      const O = this.fuelOrigin, Tg = this.fuelTarget;
      const warm = this.warm, cool = this.cool;
      for (let i = 0; i < this.K; i++) {
        const p = this.fuelParts[i];
        p.t += dt * p.sp * (0.6 + 0.8 * this.o.motion);
        if (p.t >= 1) { this.fuelParts[i] = this._spawnFuel(); this.fuelParts[i].t = 0; }
        const e = p.t * p.t * (3 - 2 * p.t); // ease
        const spread = (1 - e);
        let x = O.x + (Tg.x - O.x) * e;
        let y = O.y + (Tg.y - O.y) * e;
        let z = O.z + (Tg.z - O.z) * e;
        // swirling convergence + rising arc
        x += Math.sin(p.t * 6.283 + p.seed) * 0.34 * spread * p.ax;
        z += Math.cos(p.t * 6.283 + p.seed) * 0.30 * spread * p.ay;
        y += Math.sin(e * Math.PI) * 0.22 + Math.cos(p.t * 5.0 + p.seed) * 0.10 * spread;
        fp.array[i * 3] = x; fp.array[i * 3 + 1] = y; fp.array[i * 3 + 2] = z;
        // brightness: fade in, bright, flare as it merges
        let b = smoothstep(0, 0.10, p.t) * (1 - smoothstep(0.86, 1, p.t));
        b *= 0.7 + 0.5 * this.o.glow;
        const tc = Math.min(1, e * 1.15);
        fc.array[i * 3] = (warm.r + (cool.r - warm.r) * tc) * b;
        fc.array[i * 3 + 1] = (warm.g + (cool.g - warm.g) * tc) * b;
        fc.array[i * 3 + 2] = (warm.b + (cool.b - warm.b) * tc) * b;
      }
      fp.needsUpdate = true; fc.needsUpdate = true;
    }

    this.renderer.render(this.scene, this.camera);
    this._raf = global.requestAnimationFrame(this._frame.bind(this));
  };

  BrainHero.prototype.start = function () { if (this.running) return; this.running = true; this.last = performance.now(); this._raf = global.requestAnimationFrame(this._frame.bind(this)); };
  BrainHero.prototype.pause = function () { this.running = false; if (this._raf) global.cancelAnimationFrame(this._raf); };

  BrainHero.prototype.setColors = function (warm, cool) {
    this.warm = new T.Color(warm); this.cool = new T.Color(cool);
    this.o.warm = warm; this.o.cool = cool;
    // recolour brain points
    const col = this.brainGeo.attributes.aColor; const pos = this.brainGeo.attributes.position; const c = new T.Color();
    for (let i = 0; i < this.N; i++) {
      const z = pos.array[i * 3 + 2];
      const tcool = smoothstep(0.15, 0.85, 0.5 + 0.45 * z + 0.2 * (Math.random() - 0.5));
      c.copy(this.warm).lerp(this.cool, tcool * 0.6);
      const spark = Math.random();
      if (spark > 0.93) c.copy(this.cool).lerp(this.hot, 0.3); else if (spark > 0.88) c.copy(this.hot);
      col.array[i * 3] = c.r; col.array[i * 3 + 1] = c.g; col.array[i * 3 + 2] = c.b;
    }
    col.needsUpdate = true;
    if (this.sigMat) this.sigMat.color.copy(new T.Color(warm).lerp(this.hot, 0.5));
    if (this.emberMat) this.emberMat.color.copy(new T.Color(warm));
  };
  BrainHero.prototype.setMotion = function (v) { this.o.motion = v; };
  BrainHero.prototype.setGlow = function (v) { this.o.glow = v; };

  BrainHero.prototype.dispose = function () {
    this.pause();
    global.removeEventListener('resize', this._onResize);
    if (this.ro) this.ro.disconnect();
    if (this.o.interactive) {
      this.canvas.removeEventListener('pointermove', this._onMove);
      this.canvas.removeEventListener('pointerleave', this._onLeave);
      this.canvas.removeEventListener('pointerdown', this._onDown);
    }
    this.scene.traverse((obj) => { if (obj.geometry) obj.geometry.dispose(); if (obj.material) { if (obj.material.map) obj.material.map.dispose(); obj.material.dispose(); } });
    this.renderer.dispose();
  };

  global.BrainHero = BrainHero;
})(window);
